import { useEffect, useRef } from "react";
import { Stack, useRouter, useNavigation } from "expo-router";
import { Pressable, View, Text, ActivityIndicator, AppState, AppStateStatus } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";

/**
 * Admin layout - screens for familie_admin users
 * Protected route: redirects non-admin users to home
 */
export default function AdminLayout() {
  const router = useRouter();
  const navigation = useNavigation();
  const colors = useThemeColors();
  const { roles, isLoading, isInitialized } = useAuth();
  
  // Track if we just returned from background (e.g., image picker)
  const appState = useRef(AppState.currentState);
  const justReturnedFromBackground = useRef(false);
  const stableAdminAccess = useRef<boolean | null>(null);

  // Check if user has admin access (familie_admin or super_admin)
  const hasAdminAccess = roles.isFamilieAdmin || roles.isSuperAdmin;
  
  // Store the stable admin access state once initialized
  useEffect(() => {
    if (isInitialized && !isLoading && stableAdminAccess.current === null) {
      stableAdminAccess.current = hasAdminAccess;
    }
  }, [isInitialized, isLoading, hasAdminAccess]);
  
  // Track app state to prevent redirect when returning from background
  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState: AppStateStatus) => {
      if (appState.current.match(/inactive|background/) && nextAppState === "active") {
        // Just returned from background - skip redirect checks briefly
        justReturnedFromBackground.current = true;
        setTimeout(() => {
          justReturnedFromBackground.current = false;
        }, 2000);
      }
      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, []);

  // Redirect non-admin users to home
  useEffect(() => {
    // Don't redirect if we just returned from background (e.g., image picker)
    if (justReturnedFromBackground.current) return;
    
    // Use stable admin access if available to prevent false redirects
    if (isInitialized && !isLoading && !hasAdminAccess && stableAdminAccess.current !== true) {
      router.replace("/(tabs)");
    }
  }, [isInitialized, isLoading, hasAdminAccess, router]);

  /**
   * Handle back navigation safely
   * If there's no screen to go back to, navigate to home tabs
   */
  const handleBack = () => {
    if (navigation.canGoBack()) {
      router.back();
    } else {
      // No screen to go back to, navigate to home
      router.replace("/(tabs)");
    }
  };

  // Show loading while checking auth (but not when returning from background)
  if ((!isInitialized || isLoading) && !justReturnedFromBackground.current && stableAdminAccess.current === null) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <ActivityIndicator size="large" color={colors.primary} />
        <Text className="mt-4" style={{ color: colors.textSecondary }}>
          Laden...
        </Text>
      </View>
    );
  }

  // Don't render admin content for non-admins (will redirect via useEffect)
  // IMPORTANT: Skip this check if we just returned from background OR if we have stable admin access
  // This prevents false "no access" screens when returning from image picker
  const shouldShowNoAccess = !hasAdminAccess && 
    !justReturnedFromBackground.current && 
    stableAdminAccess.current !== true;
    
  if (shouldShowNoAccess) {
    return (
      <View
        className="flex-1 items-center justify-center"
        style={{ backgroundColor: colors.background }}
      >
        <Ionicons name="lock-closed" size={48} color={colors.error} />
        <Text className="mt-4 text-lg font-medium" style={{ color: colors.text }}>
          Geen toegang
        </Text>
        <Text className="mt-2 text-center px-8" style={{ color: colors.textSecondary }}>
          Je hebt geen toegang tot het admin panel.
        </Text>
      </View>
    );
  }

  return (
    <Stack
      screenOptions={{
        headerShown: true,
        headerStyle: { backgroundColor: colors.surface },
        headerTintColor: "#D97706",
        headerTitleStyle: { color: colors.text },
        headerLeft: () => (
          <Pressable onPress={handleBack} className="mr-2">
            <Ionicons name="arrow-back" size={24} color="#D97706" />
          </Pressable>
        ),
      }}
    >
      <Stack.Screen
        name="teams"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="games"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="algemeen"
        options={{ title: "Algemeen", headerShown: true }}
      />
      <Stack.Screen
        name="notifications"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="punten"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="guide"
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="onboarding"
        options={{
          title: "Welkom bij Admin",
          headerShown: false,
          presentation: "fullScreenModal",
        }}
      />
    </Stack>
  );
}
